import React, { useState, useMemo, useEffect, useCallback } from "react";

import { API_BASE_URL, endpoint } from "../../api/endpoint";

import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";
import { useNavigate } from "react-router-dom";

export const useReports = () => {
  const dispatch = useDispatch();

  const { authData,storeId } = useSelector((state) => state.loginSlice);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedOption, setSelectedOption] = useState("ref_txn_id");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const [filters, setFilters] = useState({ 
    settlement_created_at_from: "",
    settlement_created_at_to: "",
  });

  const [filterOptions, setFilterOptions] = useState({
   buyerAppIds: [],
    sellerAppIds: [],
  });

  const searchOptions = [
    { value: "ref_txn_id", label: "Reference Txn ID" },
    { value: "ref_msg_id", label: "Reference Msg ID" },
    { value: "settlement_id", label: "Settlement ID" },
    
  ];

 

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);


  const getReports = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${endpoint.GET_REPORTS}`;
      if (debouncedSearchValue) {
        url += `?${selectedOption}=${debouncedSearchValue}`;
      }else {
        url += `?`;
      }

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          let value = filters[key];

          if (key === "settlement_created_at_to") {
            const toDate = new Date(value);
            toDate.setHours(23, 59, 59, 999);
            value = toDate.toISOString();
          }

          if (key === "settlement_created_at_from") {
            const fromDate = new Date(value);
            fromDate.setHours(0, 0, 0, 0);
            value = fromDate.toISOString();
          }

          url += `&${key}=${value}`;
        }
      });

      if (sortConfig.key) {
        url += `&sort_by=${sortConfig.key}&sort_order=${sortConfig.direction}`;
      }

      url += `&page=${currentPage}&limit=${itemsPerPage}`;

      const result = await apiHandler({
        url: url,
        method: "GET",

        authToken: authData,
        dispatch,
        storeId
      });

      if (result?.data?.data?.reports) {
        setReports(result?.data?.data?.reports);
        setTotalOrderCount(result.data.data.pagination?.count || 0);
      } else {
        setReports([]);
        setTotalOrderCount(0);
      }

      if (result?.data?.data?.filter) {
        setFilterOptions({
            buyerAppIds: result.data.data.filter.buyer_app_ids || [],
          sellerAppIds: result.data.data.filter.seller_app_ids || [],
         
        });
      }
    } catch (error) {
      //console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchValue,
    selectedOption,
    filters,
    sortConfig,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  // --- Handlers for UI Interactions ---
  const handleDropdownChange = (e) => {
    const selectedFilter = e.target.value;
    setSelectedOption(selectedFilter);
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setCurrentPage(1);
  };

  const allowedSortKeys = [
   
    "createdAt",
  ];
  const handleSort = (key) => {
    if (!allowedSortKeys.includes(key)) return;
    setSortConfig((current) => {
      const newConfig = {
        key,
        direction:
          current.key === key && current.direction === "asc" ? "desc" : "asc",
      };
      return newConfig;
    });
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      settlement_created_at_from: "",
      settlement_created_at_to: "",
      buyer_app_id: "",
      seller_app_id: "",
     
    });
    setSearchValue("");
    setSelectedOption("ref_txn_id");
    setSortConfig({ key: null, direction: "asc" });
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const totalPages = Math.ceil(totalOrderCount / itemsPerPage);
  const startItem =
    totalOrderCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalOrderCount);

  const paginatedReports = useMemo(() => reports, [reports]);

  return {
    reports,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedReports,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    filterOptions, 
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
  };
};
